// Caching layer for Mapbox API requests
// Reduces API calls and costs by caching search and reverse geocoding results

class MapboxCache {
  constructor(ttl = 5 * 60 * 1000) {
    this.cache = new Map();
    this.ttl = ttl;
    this.maxEntries = 100; // Limit cache size to prevent memory issues
  }

  // Generate cache key based on request type and parameters
  generateKey(type, params) {
    switch (type) {
      case 'suggest':
        // Include query and proximity in cache key
        return `suggest:${params.q}:${params.proximity || 'none'}:${params.language || 'en'}`;
      
      case 'reverse':
        // Round coordinates to ~11m precision to catch nearby requests
        const lat = Math.round(parseFloat(params.latitude) * 10000) / 10000;
        const lng = Math.round(parseFloat(params.longitude) * 10000) / 10000;
        return `reverse:${lng}:${lat}:${params.language || 'en'}`;
      
      case 'retrieve':
        // Details are unique per suggestion ID
        return `retrieve:${params.suggestionId}`;
      
      default:
        // Fallback for any other request types
        return `${type}:${JSON.stringify(params)}`;
    }
  }

  // Get cached data if available and not expired
  get(type, params) {
    const key = this.generateKey(type, params);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    // Check if entry has expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    // Update access time for LRU-like behavior
    entry.lastAccessed = Date.now();
    return entry.data;
  }

  // Store data in cache
  set(type, params, data) {
    // Enforce cache size limit using LRU eviction
    if (this.cache.size >= this.maxEntries) {
      // Remove least recently accessed entry
      const lruKey = [...this.cache.entries()]
        .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed)[0][0];
      this.cache.delete(lruKey);
    }
    
    const key = this.generateKey(type, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      lastAccessed: Date.now()
    });
  }

  // Clear all cached entries
  clear() {
    this.cache.clear();
  }

  // Get cache statistics for debugging
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const [, entry] of this.cache) {
      if (now - entry.timestamp <= this.ttl) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }
    
    return {
      size: this.cache.size,
      validEntries,
      expiredEntries,
      maxEntries: this.maxEntries
    };
  }
}

// Create separate cache instances for different request types with appropriate TTLs
export const searchCache = new MapboxCache(5 * 60 * 1000); // 5 minutes for search results
export const reverseCache = new MapboxCache(10 * 60 * 1000); // 10 minutes for reverse geocoding
export const detailsCache = new MapboxCache(30 * 60 * 1000); // 30 minutes for location details

// Utility to clear all caches (useful for testing or memory management)
export function clearAllCaches() {
  searchCache.clear();
  reverseCache.clear();
  detailsCache.clear();
}